import React, { useState, useEffect } from 'react';
import { MdSearch, MdBadge, MdPerson, MdEmail, MdPhone, MdHistory } from 'react-icons/md';
import { idCardHistoryApi } from '../../../api/helpdeskApi';
import { toast } from 'react-toastify';
import '../Admin.css';
import './IdCardHistorySearch.css';

function IdCardHistorySearch() {
  const [searchInput, setSearchInput] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    idCardHistoryApi.getCount().then(d => setTotalCount(d.total)).catch(() => {});
  }, []);

  const handleSearch = async () => {
    const q = searchInput.trim();
    if (!q) { toast.warning('Enter an email, name, or employee ID to search'); return; }
    if (q.length < 2) { toast.warning('Enter at least 2 characters'); return; }
    setIsLoading(true);
    setHasSearched(true);
    try {
      let data;
      if (q.includes('@')) {
        data = await idCardHistoryApi.getByEmail(q);
      } else {
        data = await idCardHistoryApi.search(q);
      }
      setResults(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error('Search failed. Please try again.');
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const statusLabel = (s) => {
    const map = {
      // Legacy values (existing DB records)
      DRAFT: 'Draft', SUBMITTED: 'Submitted', PROCESSING: 'Processing', PRINTED: 'Printed', COMPLETED: 'Completed',
      // New values
      REGISTERED: 'Registered',
      SENT_FOR_PRINT: 'Sent for Print',
      RECEIVED_FROM_VENDOR: 'Received from Vendor',
      DELIVERED: 'Delivered to User',
      YET_TO_REGISTER: 'Yet to Register',
      UNKNOWN: 'Unknown'
    };
    return map[s] || s;
  };

  const statusClass = (s) => {
    const map = {
      // Legacy values
      DRAFT: 'draft', SUBMITTED: 'submitted', PROCESSING: 'processing', PRINTED: 'printed', COMPLETED: 'completed',
      // New values
      REGISTERED: 'open',
      SENT_FOR_PRINT: 'in-progress',
      RECEIVED_FROM_VENDOR: 'assigned',
      DELIVERED: 'resolved',
      YET_TO_REGISTER: 'draft',
      UNKNOWN: 'draft'
    };
    return map[s] || 'draft';
  };

  return (
    <div className="id-card-history-search">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-title-group">
          <h2><MdBadge /> ID Card History</h2>
          <p className="page-subtitle">Search legacy ID card requests imported from the old system</p>
        </div>
        {totalCount !== null && (
          <div className="total-count-chip">
            <MdHistory />
            <span>{totalCount.toLocaleString()} legacy records</span>
          </div>
        )}
      </div>

      {/* Search Bar */}
      <div className="search-section">
        <div className="search-input-wrapper">
          <MdSearch className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Search by employee email, name, or employee ID..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            autoFocus
          />
          {searchInput && (
            <button className="clear-btn" onClick={() => { setSearchInput(''); setResults([]); setHasSearched(false); }}>✕</button>
          )}
        </div>
        <button className="btn btn-primary search-btn" onClick={handleSearch} disabled={isLoading}>
          {isLoading ? 'Searching...' : 'Search'}
        </button>
      </div>

      <p className="search-hint">
        Tip: Enter a full email (e.g. <em>john.doe@corrohealth.com</em>), a partial name (e.g. <em>Anjali</em>), or an employee ID (e.g. <em>200700</em>)
      </p>

      {/* Results */}
      {isLoading && (
        <div className="loading-state">
          <div className="spinner" />
          <span>Searching records...</span>
        </div>
      )}

      {!isLoading && hasSearched && results.length === 0 && (
        <div className="empty-state">
          <MdBadge className="empty-icon" />
          <h3>No records found</h3>
          <p>No legacy ID card records match "<strong>{searchInput}</strong>". Try a different email, name, or employee ID.</p>
        </div>
      )}

      {!isLoading && !hasSearched && (
        <div className="initial-state">
          <MdBadge className="initial-icon" />
          <p>Enter an employee's email, name, or ID above to look up their ID card request history from the old system.</p>
        </div>
      )}

      {!isLoading && results.length > 0 && (
        <div className="results-section">
          <p className="results-count">{results.length} record{results.length !== 1 ? 's' : ''} found</p>
          <div className="results-grid">
            {results.map(rec => (
              <div
                key={rec.id}
                className="record-card"
                onClick={() => setSelectedRecord(rec)}
              >
                <div className="record-card-top">
                  <div className="record-avatar">
                    {(rec.employeeName || '?')[0].toUpperCase()}
                  </div>
                  <div className="record-info">
                    <div className="record-name">{rec.employeeName || '-'}</div>
                    <div className="record-empid">EMP ID: {rec.employeeId || '-'}</div>
                  </div>
                  <span className={`status-chip ${statusClass(rec.status)}`}>
                    {statusLabel(rec.status)}
                  </span>
                </div>
                <div className="record-card-body">
                  <div className="record-field">
                    <MdEmail className="field-icon" />
                    <span>{rec.employeeEmail || '-'}</span>
                  </div>
                  <div className="record-field">
                    <MdPhone className="field-icon" />
                    <span>{rec.contact || '-'}</span>
                  </div>
                  <div className="record-field blood-group">
                    <span className="bg-label">Blood Group</span>
                    <span className="bg-value">{rec.bloodGroup || '-'}</span>
                  </div>
                </div>
                <div className="record-card-footer">
                  Submitted: {rec.legacyCreatedAt
                    ? new Date(rec.legacyCreatedAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' })
                    : '-'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedRecord && (
        <div className="modal-overlay" onClick={() => setSelectedRecord(null)}>
          <div className="detail-modal" onClick={e => e.stopPropagation()}>
            <div className="detail-modal-header">
              <div className="detail-avatar">
                {(selectedRecord.employeeName || '?')[0].toUpperCase()}
              </div>
              <div>
                <h3>{selectedRecord.employeeName}</h3>
                <p>{selectedRecord.employeeEmail}</p>
              </div>
              <span className={`status-chip ${statusClass(selectedRecord.status)}`}>
                {statusLabel(selectedRecord.status)}
              </span>
            </div>

            <div className="detail-grid">
              <div className="detail-field">
                <span className="detail-label">Employee ID</span>
                <span className="detail-value">{selectedRecord.employeeId || '-'}</span>
              </div>
              <div className="detail-field">
                <span className="detail-label">ID Card Status</span>
                <span className="detail-value">
                  <span className={`status-chip ${selectedRecord.idCardStatus === 'DELIVERED' ? 'resolved' : selectedRecord.idCardStatus === 'PRINT_READY' ? 'assigned' : selectedRecord.idCardStatus === 'PRINT_QUEUE' ? 'in-progress' : 'open'}`}>
                    {selectedRecord.idCardStatus ? selectedRecord.idCardStatus.replace(/_/g, ' ') : '-'}
                  </span>
                </span>
              </div>
              <div className="detail-field">
                <span className="detail-label">Update Status</span>
                <span className="detail-value">
                  <span className={`status-chip ${selectedRecord.updateStatus === 'CLOSED' ? 'resolved' : selectedRecord.updateStatus === 'IN_PROGRESS' ? 'in-progress' : 'open'}`}>
                    {selectedRecord.updateStatus ? selectedRecord.updateStatus.replace(/_/g, ' ') : '-'}
                  </span>
                </span>
              </div>
              <div className="detail-field">
                <span className="detail-label">Blood Group</span>
                <span className="detail-value highlight">{selectedRecord.bloodGroup || '-'}</span>
              </div>
              <div className="detail-field">
                <span className="detail-label">Contact</span>
                <span className="detail-value">{selectedRecord.contact || '-'}</span>
              </div>
              <div className="detail-field">
                <span className="detail-label">Emergency Contact</span>
                <span className="detail-value">{selectedRecord.emergencyContact || '-'}</span>
              </div>
              <div className="detail-field full-width">
                <span className="detail-label">Address</span>
                <span className="detail-value">{selectedRecord.address || '-'}</span>
              </div>
              {selectedRecord.imageName && (
                <div className="detail-field">
                  <span className="detail-label">Photo File</span>
                  <span className="detail-value mono">{selectedRecord.imageName}</span>
                </div>
              )}
              <div className="detail-field">
                <span className="detail-label">Submitted On</span>
                <span className="detail-value">
                  {selectedRecord.legacyCreatedAt
                    ? new Date(selectedRecord.legacyCreatedAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
                    : '-'}
                </span>
              </div>
              <div className="detail-field">
                <span className="detail-label">Last Updated</span>
                <span className="detail-value">
                  {selectedRecord.legacyUpdatedAt
                    ? new Date(selectedRecord.legacyUpdatedAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
                    : '-'}
                </span>
              </div>
              {selectedRecord.reprocessReason && (
                <div className="detail-field full-width">
                  <span className="detail-label">Reprocess Reason</span>
                  <span className="detail-value">{selectedRecord.reprocessReason}</span>
                </div>
              )}
            </div>

            <div className="detail-modal-footer">
              <button className="btn btn-secondary" onClick={() => setSelectedRecord(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default IdCardHistorySearch;
